import PropTypes from "prop-types";
import React from "react";
import {
  Box,
  TextField,
  FormHelperText,
  CircularProgress
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useField, useFormikContext } from "formik";
import { Autocomplete } from "@mui/material";
import { Tooltip } from "../Tooltip";

const useStyles = makeStyles(theme => ({
  loadingSpinner: {
    marginRight: theme.spacing(1.5)
  },
  popupIndicator: {
    marginRight: theme.spacing(1)
  }
}));

const ComboBox = ({
  options,
  disabled,
  placeholder,
  isLoadingOptions,
  label,
  disableUnderline,
  tooltip,
  autocompleteProps,
  ...props
}) => {
  const classes = useStyles();

  const [{ value }, { error }, { setValue, setTouched }] = useField(props);

  const { isSubmitting, submitCount } = useFormikContext();

  const showError = !!error && submitCount > 0;
  const isDisabled = isSubmitting || disabled;
  const comboBox = (
    <Box display="flex" flexDirection="column">
      <Autocomplete
        autoHighlight
        classes={{ popupIndicator: classes.popupIndicator }}
        options={options}
        fullWidth
        renderOption={(props, option) => <li {...props}>{option.label}</li>}
        getOptionLabel={option => {
          if (option?.label) {
            return option.label;
          } else {
            const selectedOption = options.find(item => item.value === option);
            return selectedOption ? selectedOption.label : "";
          }
        }}
        value={value || null}
        onChange={(e, option) => {
          setValue(option ? option.value : null);
        }}
        isOptionEqualToValue={(option, newValue) => {
          const selectedOption = options.find(item => item.value === newValue);
          return selectedOption;
        }}
        renderInput={params => {
          return (
            <TextField
              {...params}
              onChange={e => {
                setTouched(true);
              }}
              label={label}
              hiddenLabel={!label}
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: isLoadingOptions ? (
                  <div>
                    <CircularProgress
                      size={22}
                      className={[classes.loadingSpinner]}
                      data-qa="combo-box-loading-spinner"
                    />
                  </div>
                ) : (
                  params.InputProps.endAdornment
                ),
                inputProps: {
                  ...params.inputProps,
                  "data-qa": "combo-box-input",
                  autoComplete: "none"
                },
                disableUnderline
              }}
              placeholder={placeholder}
              variant="filled"
              error={showError}
            />
          );
        }}
        disabled={isDisabled}
        data-qa="combo-box-autocomplete"
        {...autocompleteProps}
      />

      {showError && (
        <Box ml={1.5} mt={0.25}>
          <FormHelperText error data-qa={`helper-text-${props?.name}`}>
            {error}
          </FormHelperText>
        </Box>
      )}
    </Box>
  );
  return !!tooltip ? (
    <Tooltip title={tooltip}>
      <div>{comboBox}</div>
    </Tooltip>
  ) : (
    comboBox
  );
};

export default ComboBox;

ComboBox.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  isLoadingOptions: PropTypes.bool,
  name: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  disableUnderline: PropTypes.bool
};

ComboBox.defaultProps = {
  disabled: false,
  disableUnderline: true,
  tooltip: ""
};
